import React from "react";
import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import {
  VisiteModel,
  DonneesVisitesSpecifique,
} from "../../../domaine/visiteModel"; // Adjust the import path to match your project structure

// Define the interface for project options
interface Option {
  label: string;
  value: string;
}

// Provided project options
const typeDeProjetOptions: Record<string, Option[]> = {
  appartements: [
    { label: "Studios", value: "Studios" },
    { label: "F2", value: "F2" },
    { label: "F3", value: "F3" },
    { label: "F4", value: "F4" },
    { label: "F5", value: "F5" },
    { label: "Duplex", value: "Duplex" },
    { label: "Triplex", value: "Triplex" },
  ],
  bureaux: [
    { label: "Coworking", value: "Coworking" },
    { label: "Salles de conférences", value: "Salles de conférences" },
    { label: "Bureaux individuels", value: "Bureaux individuels" },
    { label: "Open space", value: "Open space" },
  ],
  villas: [
    { label: "Isolées", value: "Isolées" },
    { label: "Jumelées", value: "Jumelées" },
    { label: "En bande", value: "En bande" },
  ],
  locaux_commerciaux: [
    { label: "Centres commerciaux", value: "Centres commerciaux" },
    { label: "Pied d'immeuble", value: "Pied d'immeuble" },
    { label: "Showrooms", value: "Showrooms" },
  ],
};

// Define color palettes for each category
const colorPalettes: Record<string, string[]> = {
  appartements: [
    "#dbadff",
    "#d588ff",
    "#b978fa",
    "#690fef",
    "#6d11d7",
    "#670db2",
    "#640899",
  ],
  bureaux: [
    "#FFCCCC",
    "#FF9999",
    "#FF6666",
    "#FF3333",
    "#CC0000",
    "#990000",
    "#660000",
  ],
  villas: [
    "#CCFFCC",
    "#99FF99",
    "#66FF66",
    "#33FF33",
    "#00CC00",
    "#009900",
    "#006600",
  ],
  locaux_commerciaux: [
    "#00FFFF", // Cyan
    "#00E6E6", // Slightly darker cyan
    "#00CCCC", // Darker cyan
    "#00B3B3", // Even darker cyan
    "#009999", // Dark cyan
    "#008080", // Darker cyan
    "#006666", // Very dark cyan
  ],
};

// Define the interface for chart data points
interface ChartData {
  name: string;
  value: number;
  itemStyle?: {
    color: string;
  };
  label?: {
    show: boolean;
  };
}

interface NestedPieChartProps {
  visite?: VisiteModel; // Make visite optional
  trancheId?: number; // Optional tranche ID to display in no-data case
}

const NestedPieChart: React.FC<NestedPieChartProps> = ({
  visite,
  trancheId,
}) => {
  // Function to generate dynamic data based on visite data
  const generateDynamicData = (): {
    innerData: ChartData[];
    outerData: ChartData[];
  } => {
    const innerData: ChartData[] = [];
    const outerData: ChartData[] = [];

    // Check if visite exists
    if (!visite || !visite.donnees_visites_specifiques) {
      // Return empty data if visite is undefined
      return { innerData, outerData };
    }

    // Group data by programming type
    const groupedData: Record<string, DonneesVisitesSpecifique[]> = {
      appartements: [],
      bureaux: [],
      villas: [],
      locaux_commerciaux: [],
    };

    // Filter and group specific visit data by programming type
    visite.donnees_visites_specifiques.forEach((item) => {
      if (
        item.programmation_type === "appartements" &&
        item.fk_programmation_appartement_detail
      ) {
        groupedData.appartements.push(item);
      } else if (
        item.programmation_type === "bureaux" &&
        item.fk_programmation_bureau_detail
      ) {
        groupedData.bureaux.push(item);
      } else if (
        item.programmation_type === "villas" &&
        item.fk_programmation_villas_detail
      ) {
        groupedData.villas.push(item);
      } else if (
        item.programmation_type === "lc" &&
        item.fk_programmation_lc_detail
      ) {
        groupedData.locaux_commerciaux.push(item);
      }
    });

    // Process each category
    Object.entries(typeDeProjetOptions).forEach(([category, options]) => {
      const palette = colorPalettes[category];
      let categoryTotal = 0;

      // Create a tracking map for options that have data
      const optionValueMap = new Map<string, number>();
      options.forEach((option) => optionValueMap.set(option.value, 0));

      // Calculate values for each option based on actual data
      if (category === "appartements") {
        groupedData.appartements.forEach((item) => {
          const detail = item.fk_programmation_appartement_detail;
          if (detail) {
            // Map the detail to one of our predefined options based on name or characteristics
            let optionKey = "Studios"; // Default

            if (detail.name.includes("F2")) {
              optionKey = "F2";
            } else if (detail.name.includes("F3")) {
              optionKey = "F3";
            } else if (detail.name.includes("F4")) {
              optionKey = "F4";
            } else if (detail.name.includes("F5")) {
              optionKey = "F5";
            } else if (detail.name.toLowerCase().includes("duplex")) {
              optionKey = "Duplex";
            } else if (detail.name.toLowerCase().includes("triplex")) {
              optionKey = "Triplex";
            } else if (detail.name.toLowerCase().includes("studios")) {
              optionKey = "Studios";
            }

            // Add the units sold to the corresponding option
            const currentValue = optionValueMap.get(optionKey) || 0;
            optionValueMap.set(optionKey, currentValue + item.nbr_unites_total);
            categoryTotal += item.nbr_unites_total;
          }
        });
      } else if (category === "bureaux") {
        groupedData.bureaux.forEach((item) => {
          const detail = item.fk_programmation_bureau_detail;
          if (detail) {
            // Map bureau details to options
            let optionKey = "Bureaux individuels"; // Default

            if (detail.name.toLowerCase().includes("coworking")) {
              optionKey = "Coworking";
            } else if (
              detail.name.toLowerCase().includes("salles de conférences")
            ) {
              optionKey = "Salles de conférences";
            } else if (detail.name.toLowerCase().includes("open space")) {
              optionKey = "Open space";
            }

            const currentValue = optionValueMap.get(optionKey) || 0;
            optionValueMap.set(optionKey, currentValue + item.nbr_unites_total);
            categoryTotal += item.nbr_unites_total;
          }
        });
      } else if (category === "villas") {
        groupedData.villas.forEach((item) => {
          const detail = item.fk_programmation_villas_detail;
          if (detail) {
            // Map villa details to options
            let optionKey = "Isolées"; // Default

            if (detail.name.toLowerCase().includes("jumelées")) {
              optionKey = "Jumelées";
            } else if (detail.name.toLowerCase().includes("en bande")) {
              optionKey = "En bande";
            } else if (detail.name.toLowerCase().includes("isolées")) {
              optionKey = "Isolées";
            }

            const currentValue = optionValueMap.get(optionKey) || 0;
            optionValueMap.set(optionKey, currentValue + item.nbr_unites_total);
            categoryTotal += item.nbr_unites_total;
          }
        });
      } else if (category === "locaux_commerciaux") {
        groupedData.locaux_commerciaux.forEach((item) => {
          const detail = item.fk_programmation_lc_detail;
          if (detail) {
            // Map commercial space details to options
            let optionKey = "Pied d'immeuble"; // Default

            if (detail.name.toLowerCase().includes("centres commerciaux")) {
              optionKey = "Centres commerciaux";
            } else if (detail.name.toLowerCase().includes("showrooms")) {
              optionKey = "Showrooms";
            } else if (detail.name.toLowerCase().includes("pied d'immeuble")) {
              optionKey = "Pied d'immeuble";
            }

            const currentValue = optionValueMap.get(optionKey) || 0;
            optionValueMap.set(optionKey, currentValue + item.nbr_unites_total);
            categoryTotal += item.nbr_unites_total;
          }
        });
      }

      // Generate outer chart data for each option in the category
      options.forEach((option, index) => {
        const value = optionValueMap.get(option.value) || 0;
        if (value > 0) {
          outerData.push({
            name: option.label,
            value,
            itemStyle: {
              color: palette[index % palette.length],
            },
            label: {
              show: true,
            },
          });
        }
      });

      // Add inner chart data for the category if it has any values
      if (categoryTotal > 0) {
        innerData.push({
          name: category,
          value: categoryTotal,
          itemStyle: {
            color: palette[0],
          },
          label: {
            show: true,
          },
        });
      }
    });

    return { innerData, outerData };
  };
  const { innerData, outerData } = generateDynamicData();

  // Handle empty data case
  if (innerData.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "40px 20px",
          border: "1px dashed #ccc",
          borderRadius: "8px",
          color: "#999",
        }}
      >
        <p>
          Aucune donnée de vente disponible pour cette tranche
          {trancheId ? ` #${trancheId}` : ""}
        </p>
      </div>
    );
  }

  const option: EChartsOption = {
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b}: {c} unités",
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: "Composant",
        type: "pie",
        selectedMode: "single",
        radius: ["20%", "50%"],
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          position: "inside",
          formatter: "{b}\n{c} unités",
        },
        labelLine: {
          show: false,
        },
        data: innerData,
      },
      {
        name: "Programmation",
        type: "pie",
        radius: ["40%", "65%"],
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: true,
          formatter: "{b}",
        },
        data: outerData,
      },
    ],
  };

  return (
    <ReactEcharts option={option} style={{ height: "400px", width: "100%" }} />
  );
};

export default NestedPieChart;
